import { useState } from 'react';
import {
  getComments,
  createComment,
  updateComment,
  uploadimgticket,
  getimgticket
} from '../../../../../services/supports/supports';
import { showAlert } from '../../../../../main_components/alerts/alert_response';

interface CommentData {
  comment: string;
  reply: number | null;
  edited: boolean;
  date: string | null;
  activate_alert: boolean;
  files: string[];
  user_name?: string;
  support_staff?: boolean;
}

interface AttachedFile {
  file: File;
  renamedName: string;
}

export const useComments = ({
  ticketId,
  ticketData,
  onTicketUpdate,
  commentInputRef,
  notificationSystemRef,
  attachedFiles,
  setAttachedFiles,
  setDocsData,
  getCurrentTimestamp,
}: {
  ticketId: string;
  ticketData: any;
  onTicketUpdate?: (updatedTicket: any) => void;
  commentInputRef: any;
  notificationSystemRef: any;
  attachedFiles: AttachedFile[];
  setAttachedFiles: React.Dispatch<React.SetStateAction<AttachedFile[]>>;
  setDocsData: React.Dispatch<any>;
  getCurrentTimestamp: () => string;
}) => {
  const [comments, setComments] = useState<any[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [error, setError] = useState(false);
  const [replyTo, setReplyTo] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingComment, setEditingComment] = useState<any | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getLast3CommentsByDate = (comments: any[]) => {
    return [...comments]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 3);
  };

  const downloadFilesFromLast3Comments = async (
    comments: any[]
  ): Promise<{ file: string; file_name: string }[]> => {
    const result: { file: string; file_name: any }[] = [];
    const downloadPromises: Promise<void>[] = [];

    for (const comment of comments) {
      if (Array.isArray(comment.files)) {
        for (const fileName of comment.files) {
          const promise = getimgticket(ticketId, fileName)
            .then((res) => {
              result.push({
                file_name: res.data.file_name,
                file: res.data.file,
              });
            })
            .catch((err) => {
              console.warn(`❌ Error downloading file: ${fileName}`, err);
            });
          downloadPromises.push(promise);
        }
      }
    }

    await Promise.all(downloadPromises);
    return result;
  };

  const get_comments = async () => {
    setLoadingComments(true);
    try {
      const response = await getComments(ticketId);
      const latestFive = getLast3CommentsByDate(response.data);
      const base64Files = await downloadFilesFromLast3Comments(latestFive);

      setDocsData(base64Files);
      setComments(response.data);
      setError(false);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setComments([]);
        setError(true);
      } else {
        console.error("Error al obtener comentarios:", err);
      }
    } finally {
      setLoadingComments(false);
    }
  };

  const getCommentDepth = (
    id: number | null,
    all: any[],
    currentDepth = 0
  ): number => {
    if (id === null) return currentDepth;
    const parent = all.find((c) => c.id_comment === id);
    if (!parent) return currentDepth;
    return getCommentDepth(parent.reply, all, currentDepth + 1);
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result === 'string') {
          const base64String = result.split(',')[1];
          resolve(base64String || '');
        } else {
          reject(new Error('Formato inválido'));
        }
      };
      reader.onerror = () => reject(new Error('Error al leer archivo'));
      reader.readAsDataURL(file);
    });
  };

  const handleSubmitComment = async () => {
    let comment = commentInputRef.current?.getValue().trim() || '';

    if (!comment && attachedFiles.length === 0) {
      showAlert({
        msj: 'Proporciona un comentario, audio o archivo antes de mandar el mensaje.',
        status: false,
        notificationSystemRef,
      });
      return;
    }

    if (!comment && attachedFiles.length > 0) {
      comment = 'Se cargaron archivos.';
    }

    setIsSubmitting(true);

    const commentDepth = getCommentDepth(replyTo, comments);
    if (commentDepth >= 2) {
      alert('No puedes responder más allá del tercer nivel.');
      setIsSubmitting(false);
      return;
    }

    const commentData: CommentData = {
      comment,
      reply: replyTo,
      edited: isEditing,
      date: isEditing && editingComment ? editingComment.date : null,
      activate_alert: attachedFiles.length === 0,
      files: [],
    };

    let createdCommentId: number | null = null;
    let createdDate = '';
    let finalCommentData: CommentData = { ...commentData };
    let newComment: any = null;

    try {
      if (isEditing && editingComment) {
        await updateComment(ticketId, commentData);
      } else {
        const response = await createComment(ticketId, commentData);
        createdCommentId = response.data.id_comment;
        createdDate = response.data.item.date;

        finalCommentData = {
          ...commentData,
          date: createdDate,
          user_name: response.data.item.user_name,
          support_staff: response.data.item.support_staff,
          files: [],
        };

        newComment = {
          ...finalCommentData,
          id_comment: createdCommentId,
          responses: [],
        };
      }

      if (attachedFiles.length > 0 && (!isEditing || editingComment)) {
        const uploadResponses: any[] = [];

        for (const attachment of attachedFiles) {
          const base64 = await fileToBase64(attachment.file);
          const response = await uploadimgticket(ticketId, {
            file_name: attachment.renamedName,
            file_b64: base64,
          });
          uploadResponses.push({ file_name: response.data.file_name, file: base64 });
        }

        const uploadedFileNames = uploadResponses.map((res) => res.file_name);
        finalCommentData.files = uploadedFileNames;
        finalCommentData.activate_alert = true;

        if (isEditing && editingComment) {
          await updateComment(ticketId, finalCommentData);
        } else if (createdCommentId !== null) {
          await updateComment(ticketId, {
            ...finalCommentData,
            id_comment: createdCommentId,
          });
        }

        setDocsData((prev: any[]) => [...(prev || []), ...uploadResponses]);

        if (!isEditing && newComment) {
          newComment.files = uploadedFileNames;
        }
      }

      if (isEditing && editingComment) {
        setComments((prev) =>
          prev.map((c) =>
            c.id_comment === editingComment.id_comment ? { ...c, ...finalCommentData } : c
          )
        );
      } else if (newComment) {
        if (replyTo !== null) {
          setComments((prev) =>
            prev.map((c) =>
              c.id_comment === replyTo
                ? { ...c, responses: [...(c.responses || []), newComment] }
                : c
            )
          );
        } else {
          setComments((prev) => [...prev, newComment]);
          setError(false);
        }
      }

      if (typeof onTicketUpdate === 'function') {
        onTicketUpdate({
          ...ticketData,
          last_update: getCurrentTimestamp(),
        });
      }

      setReplyTo(null);
      setIsEditing(false);
      setEditingComment(null);
      setAttachedFiles([]);
    } catch (error: any) {
      console.error('Error submitting comment:', error);
      showAlert({
        msj: error.response?.data?.errorMessage || 'Error al enviar el comentario.',
        status: false,
        notificationSystemRef,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReply = (id: number) => {
    setReplyTo(id);
    setIsEditing(false);
    setEditingComment(null);
  };

  const handleEdit = (commentObj: any) => {
    setIsEditing(true);
    setEditingComment(commentObj);
    setReplyTo(null);
  };

  return {
    comments,
    loadingComments,
    error,
    replyTo,
    isEditing,
    editingComment,
    isSubmitting,
    get_comments,
    handleSubmitComment,
    handleReply,
    handleEdit,
    setReplyTo,
    setIsEditing,
    setEditingComment,
    setLoadingComments,
    setIsSubmitting,
    setComments,
  };
};
